// Simple AI Image Generator

import React, { useState, useRef, useEffect } from 'react';
import {
   Box,
   Button,
   TextField,
   Typography,
   Paper,
   Grid,
   Chip,
   CircularProgress,
   Alert,
   IconButton,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   FormControl,
   InputLabel,
   Select,
   MenuItem,
   SelectChangeEvent,
} from '@mui/material';
import {
   AutoAwesome as AutoAwesomeIcon,
   Close as CloseIcon,
   Refresh as RefreshIcon,
   PhotoLibrary as PhotoLibraryIcon,
   CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import {
   createEventCoverPrompt,
   getEventImageNegativePrompt,
   base64ToFile,
} from '../../services/stabilityAIService-new';
import useImageTransform from '../../hooks/useImageGen';
import { convertToJPEG } from '../../utils/imageUtils';

// Basic props interface
interface AIImageGeneratorProps {
   open: boolean;
   onClose: () => void;
   eventTitle: string;
   eventDescription?: string;
   eventType?: string;
   onImageGenerated: (file: File | null, previewUrl: string | null) => void;
}

// Style options
const styleOptions = [
   { value: 'realistic_image', label: 'Realistic Image' },
   { value: 'digital_illustration', label: 'Digital Illustration' },
   { value: 'vector_illustration', label: 'Vector Illustration' },
];

const AIImageGenerator: React.FC<AIImageGeneratorProps> = ({
   open,
   onClose,
   eventTitle,
   eventDescription,
   eventType,
   onImageGenerated,
}) => {
   const [customPrompt, setCustomPrompt] = useState('');
   const [stylePreset, setStylePreset] = useState<string>('realistic_image');
   // const [additionalElements, setAdditionalElements] = useState<string[]>([]);
   const [isGenerating, setIsGenerating] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [generatedImage, setGeneratedImage] = useState('');
   const { generateImage } = useImageTransform();

   // // Generated images state
   // const [generatedImages, setGeneratedImages] = useState<Array<{
   //   base64: string;
   //   dataUrl: string;
   //   seed: number;
   // }>>([]);
   // const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

   // // Abort controller reference for cancelling fetch requests
   // const abortControllerRef = useRef<AbortController | null>(null);
   // // Timeout reference for handling request timeouts
   // const timeoutRef = useRef<NodeJS.Timeout | null>(null);

   // // Reset state when dialog is closed
   // useEffect(() => {
   //   if (!open) {
   //     // Reset state only if the dialog is closing
   //     resetState();
   //   }
   // }, [open]);

   // // Cleanup on component unmount
   // useEffect(() => {
   //   return () => {
   //     cancelGenerationRequest();
   //     clearTimeoutRef();
   //   };
   // }, []);

   // // Handle style change
   // const handleStyleChange = (event: SelectChangeEvent) => {
   //   setStylePreset(event.target.value as StabilityAIStylePreset);
   // };

   // // Cancel any ongoing request
   // const cancelGenerationRequest = () => {
   //   if (abortControllerRef.current) {
   //     abortControllerRef.current.abort();
   //     abortControllerRef.current = null;
   //   }

   //   // Clear any pending timeouts
   //   if (timeoutRef.current) {
   //     clearTimeoutRef();
   //   }
   // };

   // // Rename the method to avoid collision with global clearTimeout
   // const clearTimeoutRef = () => {
   //   if (timeoutRef.current) {
   //     global.clearTimeout(timeoutRef.current);
   //     timeoutRef.current = null;
   //   }
   // };

   // // Reset all state to initial values
   // const resetState = () => {
   //   cancelGenerationRequest();
   //   setIsGenerating(false);
   //   setError(null);
   // };

   // // Handle dialog close
   // const handleClose = () => {
   //   // Cancel any ongoing requests first
   //   cancelGenerationRequest();
   //   // Reset generating state
   //   setIsGenerating(false);
   //   // Call the parent onClose function
   //   onClose();
   // };

   // // Generate image
   // const handleGenerateImage = async () => {
   //   try {
   //     // Cancel any previous request
   //     cancelGenerationRequest();

   //     // Reset error state
   //     setError(null);
   //     setIsGenerating(true);

   //     // Create a new AbortController
   //     abortControllerRef.current = new AbortController();

   //     // Set a timeout to prevent hanging (30 seconds)
   //     timeoutRef.current = setTimeout(() => {
   //       setIsGenerating(false);
   //       setError('Request timed out. Please try again.');
   //       cancelGenerationRequest();
   //     }, 30000);

   //     // Create prompt
   //     let prompt = createEventCoverPrompt(eventTitle, eventDescription, eventType, customPrompt);
   //     if (additionalElements.length > 0) {
   //       prompt += `, ${additionalElements.join(', ')}`;
   //     }

   //     // Call API with abort signal
   //     const response = await generateImage({
   //       prompt,
   //       negativePrompt: getEventImageNegativePrompt(),
   //       stylePreset
   //     }, abortControllerRef.current.signal);

   //     // Clear timeout since request completed
   //     clearTimeoutRef();

   //     // Handle errors
   //     if (response.error) {
   //       throw new Error(response.error);
   //     }

   //     if (response.images.length === 0) {
   //       throw new Error('No images were generated');
   //     }

   //     // Process images
   //     const processedImages = response.images.map(img => ({
   //       base64: img.base64,
   //       dataUrl: `data:image/png;base64,${img.base64}`,
   //       seed: img.seed
   //     }));

   //     setGeneratedImages(processedImages);
   //     setSelectedImageIndex(0);
   //   } catch (err: any) {
   //     // Only show error if not aborted
   //     if (err.name !== 'AbortError') {
   //       console.error('Error generating image:', err);
   //       setError(err.message || 'Failed to generate image');
   //     }
   //   } finally {
   //     setIsGenerating(false);
   //     // Clean up abort controller
   //     abortControllerRef.current = null;
   //   }
   // };

   // // Use selected image
   // const handleUseSelectedImage = () => {
   //   if (selectedImageIndex !== null && generatedImages[selectedImageIndex]) {
   //     const selectedImage = generatedImages[selectedImageIndex];

   //     try {
   //        // Create a file from the base64 image data
   //        const imageFile = base64ToFile(selectedImage.base64, `ai-generated-${Date.now()}.png`);

   //        // Pass both the file and dataUrl to the parent component
   //        onImageGenerated(imageFile, selectedImage.dataUrl);
   //        onClose();
   //     } catch (error) {
   //       console.error('Error creating file from base64 data:', error);
   //       setError('Failed to process generated image. Please try again.');
   //     }
   //   }
   // };

   const handleStyleChange = (event: SelectChangeEvent) => {
      setStylePreset(event.target.value as string);
   };

   const handleClose = () => {
      onClose();
   };

   const handleGenerateImage = async () => {
      try {
         setIsGenerating(true);
         setError(null);
         const response = await generateImage({
            prompt: createEventCoverPrompt(eventTitle, eventDescription, eventType, customPrompt),
            style: stylePreset,
            substyle: '',
            num_images: 1,
         });

         if (response.images && response.images.length > 0) {
            setGeneratedImage(response.images[0].url);
         } else {
            setError('No images were generated');
         }
      } catch (error: any) {
         setError(error.message || 'Failed to generate image');
      } finally {
         setIsGenerating(false);
      }
   };

   const handleUseSelectedImage = async () => {
      if (onImageGenerated && generatedImage) {
         const { blob, extension } = await convertToJPEG(generatedImage);
         const file = new File([blob], `ai-generated-${Date.now()}.${extension}`, {
            type: `image/${extension}`,
         });
         onImageGenerated(file, generatedImage);
         onClose();
      }
   };

   const cancelGenerationRequest = () => {
      setIsGenerating(false);
      if (onImageGenerated) {
         onImageGenerated(null, null);
      }
   };

   return (
      <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
         <DialogTitle>
            <Box display='flex' alignItems='center' justifyContent='space-between'>
               <Box display='flex' alignItems='center'>
                  <AutoAwesomeIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant='h6'>Generate Cover with AI</Typography>
               </Box>
               <IconButton onClick={handleClose} size='small'>
                  <CloseIcon />
               </IconButton>
            </Box>
         </DialogTitle>

         <DialogContent dividers>
            <Grid container spacing={3}>
               {/* Settings section */}
               <Grid item xs={12} md={6}>
                  <TextField
                     label='Additional Description'
                     placeholder='Describe what you want to see in the image'
                     multiline
                     rows={2}
                     fullWidth
                     margin='normal'
                     value={customPrompt}
                     onChange={(e) => setCustomPrompt(e.target.value)}
                  />

                  <FormControl fullWidth margin='normal'>
                     <InputLabel id='style-preset-label'>Visual Style</InputLabel>
                     <Select
                        labelId='style-preset-label'
                        value={stylePreset}
                        label='Visual Style'
                        onChange={handleStyleChange}
                     >
                        {styleOptions.map((style) => (
                           <MenuItem key={style.value} value={style.value}>
                              {style.label}
                           </MenuItem>
                        ))}
                     </Select>
                  </FormControl>
               </Grid>

               {/* Preview section */}
               <Grid item xs={12} md={6}>
                  <Paper
                     variant='outlined'
                     sx={{
                        height: 300,
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 1,
                        overflow: 'hidden',
                        bgcolor: 'background.paper',
                     }}
                  >
                     {isGenerating ? (
                        <Box sx={{ textAlign: 'center' }}>
                           <CircularProgress sx={{ mb: 2 }} />
                           <Typography>Creating image...</Typography>
                        </Box>
                     ) : generatedImage ? (
                        <Box
                           component='img'
                           src={generatedImage}
                           alt='AI Generated'
                           style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                     ) : (
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                           <PhotoLibraryIcon
                              sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }}
                           />
                           <Typography variant='body2' color='text.secondary'>
                              Click "Generate Image" to create a cover
                           </Typography>
                        </Box>
                     )}
                  </Paper>

                  {error && (
                     <Alert severity='error' sx={{ mt: 2 }}>
                        {error}
                     </Alert>
                  )}
               </Grid>
            </Grid>
         </DialogContent>

         <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleClose}>Cancel</Button>
            {isGenerating ? (
               <Button
                  variant='outlined'
                  color='secondary'
                  onClick={cancelGenerationRequest}
                  startIcon={<CloseIcon />}
               >
                  Stop Generation
               </Button>
            ) : (
               <Button
                  variant='contained'
                  onClick={handleGenerateImage}
                  startIcon={<AutoAwesomeIcon />}
                  disabled={isGenerating}
               >
                  Generate Image
               </Button>
            )}
            {generatedImage && (
               <Button
                  variant='contained'
                  color='primary'
                  onClick={handleUseSelectedImage}
                  startIcon={<CheckCircleIcon />}
               >
                  Use This Image
               </Button>
            )}
         </DialogActions>
      </Dialog>
   );
};

export default AIImageGenerator;
